import React from "react";
import { View } from "./types";
import HomeView from "./views/HomeView";
import SpecialistsViewContainer from "./views/SpecialistsView.container";
import ServicesViewContainer from "./views/ServicesView.container";
import DateTimeViewContainer from "./views/DateTimeView.container";
import OverviewViewContainer from "./views/OverviewView.container";
import { useBookingDraft } from "./BookingDraftContext";
import ClientDashboardFlow from "../client/ClientDashboardFlow";
import SupportRequest from "../support/SupportRequest";


/**
 * This BookingFlow uses hardcoded demo data (UI-only).
 * For images, we use public placeholders to avoid cross-package asset imports.
 * When moving to production you can replace avatar strings with imported asset URLs
 * or copy the dev/widget public assets into apps/widget/src/pages/booking/assets.
 */

export default function BookingFlow({
  preselectedStaffId,
  waitlistClaimToken,
  preselectedServiceId,
  preselectedCategoryId,
}: {
  preselectedStaffId?: string | null;
  waitlistClaimToken?: string | null;
  preselectedServiceId?: string | null;
  preselectedCategoryId?: string | null;
}) {
  // Determine initial view based on preselections:
  //   staff (±service/category) → "specialist" (validates staff first, then chains)
  //   service or category       → "services"   (validates service / scrolls to category)
  //   neither                   → "main"
  const initialView: View = preselectedStaffId
    ? "specialist"
    : (preselectedServiceId || preselectedCategoryId)
      ? "services"
      : "main";
  const [view, setView] = React.useState<View>(initialView);
  const [showClientDashboard, setShowClientDashboard] = React.useState(false);
  const [showSupportModal, setShowSupportModal] = React.useState(false);

  // BookingDraft-driven CTA decisions
  const { draft, actions } = useBookingDraft();

  // Waiting-list claim mode (waitlist README §4.4): resolve the token,
  // prefill the draft (service/master/slot/contact) and jump to the
  // confirmation step. Lost race / expired → land on the calendar with the
  // master's live alternatives (standard flow).
  const claimHandled = React.useRef(false);
  React.useEffect(() => {
    if (!waitlistClaimToken || claimHandled.current) return;
    claimHandled.current = true;
    (async () => {
      try {
        const { resolveClaim } = await import("@/lib/services/waitlistPublicService");
        const { DateTime } = await import("luxon");
        const { getCompanyRegistry } = await import("@/lib/company/companyRegistrySingleton");
        const res = await resolveClaim(waitlistClaimToken);
        if (res.entry?.status !== "active") return;
        actions.setServices([String(res.entry.service_id)]);
        actions.setClientName(res.entry.client_name ?? "");
        actions.setClientPhone(res.entry.client_phone ?? "");
        if (res.entry.client_email) actions.setClientEmail(res.entry.client_email);
        actions.setClaimToken(waitlistClaimToken);
        if (res.offered_slot) {
          const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
          actions.setSpecialist(String(res.offered_slot.staff_id));
          actions.setDateISO(res.offered_slot.date);
          actions.setStartISO(
            DateTime.fromISO(`${res.offered_slot.date}T${res.offered_slot.start}`, { zone: tz })
              .toUTC().toISO());
          setView("overview");
        } else {
          actions.setSpecialist(res.entry.staff_id ? String(res.entry.staff_id) : "any");
          setView("datetime");
        }
      } catch (e) {
        console.error("[waitlist] claim resolve failed", e);
      }
    })();
  }, [waitlistClaimToken, actions]);

  const onChooseSpecialist = () => setView("specialist");
  const onChooseServices = () => setView("services");
  const onOpenClientDashboard = () => setShowClientDashboard(true);
  const onCloseClientDashboard = () => setShowClientDashboard(false);
  const onOpenSupport = () => setShowSupportModal(true);
  const onCloseSupport = () => setShowSupportModal(false);

  return (
    <>
      {/* Client Dashboard Modal */}
      {showClientDashboard && (
        <div className="fixed inset-0 z-50 bg-background">
          <ClientDashboardFlow onClose={onCloseClientDashboard} />
        </div>
      )}

      {view === "main" && (
        <HomeView
          onChooseSpecialist={onChooseSpecialist}
          onChooseServices={onChooseServices}
          onOpenClientDashboard={onOpenClientDashboard}
          onOpenSupport={onOpenSupport}
        />
      )}

      {view === "specialist" && (
        <SpecialistsViewContainer
          preselectedStaffId={preselectedStaffId}
          onAutoAdvance={() => setView("services")}
          onBack={() => { actions.reset(); setView("main"); }}
          onGoToSpecialist={() => setView("specialist")}
          onGoToServices={() => setView("services")}
          onOpenClientDashboard={onOpenClientDashboard}
          onOpenSupport={onOpenSupport}
          onContinue={() => {
            const hasSpecialist = !!draft.specialistId;
            const hasServices = (draft.serviceIds ?? []).length > 0;
            if (hasSpecialist && hasServices) {
              setView("datetime");
            } else if (hasSpecialist && !hasServices) {
              setView("services");
            } else {
              setView("services");
            }
          }}
        />
      )}

      {view === "services" && (
        <ServicesViewContainer
          preselectedServiceId={preselectedServiceId}
          preselectedCategoryId={preselectedCategoryId}
          onAutoAdvance={() => {
            // If specialist is already set (e.g. from staff preselection), skip to datetime;
            // otherwise go to specialist picker.
            if (draft.specialistId) {
              setView("datetime");
            } else {
              setView("specialist");
            }
          }}
          onBack={() => { actions.reset(); setView("main"); }}
          onGoToSpecialist={() => setView("specialist")}
          onGoToServices={() => setView("services")}
          onOpenClientDashboard={onOpenClientDashboard}
          onOpenSupport={onOpenSupport}
          onContinue={() => {
            const hasSpecialist = !!draft.specialistId;
            const hasServices = (draft.serviceIds ?? []).length > 0;
            if (hasSpecialist && hasServices) {
              setView("datetime");
            } else if (hasServices && !hasSpecialist) {
              setView("specialist");
            } else {
              setView("datetime");
            }
          }}
          onPickAnotherMaster={() => setView("specialist")}
        />
      )}

      {view === "datetime" && (
        <DateTimeViewContainer
          onBack={() => setView("services")}
          onDone={() => setView("overview")}
        />
      )}

      {view === "overview" && (
        <OverviewViewContainer
          onBack={() => setView("datetime")}
          onEditSpecialist={() => setView("specialist")}
          onEditServices={() => setView("services")}
          onEditDateTime={() => setView("datetime")}
          onRestart={() => { actions.reset(); setView("main"); }}
          onOpenClientDashboard={onOpenClientDashboard}
        />
      )}

      {/* Support Request Modal (external trigger) */}
      <SupportRequest
        externalTrigger
        isOpen={showSupportModal}
        onClose={onCloseSupport}
      />
    </>
  );
}
